import { useMemo } from 'react';
import { Database } from 'lucide-react';
import type { OverviewSprint, SprintCapacityDayState, SprintCapacitySummary } from '@hygge/shared';
import { formatDate } from '@/lib/date';
import { cn } from '@/lib/utils';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { capacityNote, sprintWorkload } from '../../lib/sprint-capacity-view';

/**
 * How many working days the sprint actually had.
 *
 * Every tracker shows the numerator — how much work is done. None can compute
 * the denominator, because the board and the leave register are in different
 * products. Here they are one database, so this band can say that a sprint
 * which looks like 150 person-days on paper only had 133 once approved and
 * pending leave is removed. That is a calendar fact: it names a person, a date
 * and an action, and nothing in it is predicted.
 *
 * Three rules the markup below is built to keep:
 *
 * - Approved and pending never merge. A pending request is still declinable;
 *   drawing it as a committed absence makes the manager's decision for them.
 *   They differ by fill AND by texture — solid versus hatched — so the strip
 *   survives a monochrome screen and every form of colour blindness.
 *
 * - The equation is shown, not just its answer. Each term carries the figures
 *   it was derived from, so a reader who disbelieves 88.7% can check it against
 *   ten working days and fifteen people without leaving the page.
 *
 * - It is not conditional on bad news. A sprint with nobody away reads 100% and
 *   keeps its band, because "everyone was here" is also an answer; a panel that
 *   appears only when something is wrong teaches readers to fear it.
 */

interface SprintCapacityBandProps {
  /** The sprint being reported on — the active one, or null when none is. */
  sprint: OverviewSprint | null;
  className?: string;
}

export function SprintCapacityBand({ sprint, className }: SprintCapacityBandProps) {
  const capacity = sprint?.capacity ?? null;
  // Which of the band's five states this is — decided in a tested pure module,
  // because a ternary chain in JSX is, in this repo, untested code.
  const note = capacityNote(sprint);

  return (
    <section className={cn('space-y-4', className)} aria-label="Sprint capacity">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Capacity
        </h2>
        {sprint?.startDate && sprint.endDate && (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {sprint.name} · {formatDate(sprint.startDate, 'monthDay')} –{' '}
            {formatDate(sprint.endDate, 'monthDay')}
          </span>
        )}
      </div>

      {note !== null ? (
        <Note>{note}</Note>
      ) : sprint && capacity ? (
        <>
          <CapacityEquation capacity={capacity} />
          <CapacityMeter capacity={capacity} />
          <DayStrip sprint={sprint} capacity={capacity} />
        </>
      ) : null}
    </section>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}

// ─── The equation ─────────────────────────────────────────────────────────

/**
 * 150 on paper − 17 to leave = 133 actual, and the share that leaves.
 *
 * Laid out as arithmetic rather than as four unrelated tiles: the operators are
 * on screen because the reader's question about any headline availability
 * figure is "out of what?", and an equation answers it before it is asked.
 */
function CapacityEquation({ capacity }: { capacity: SprintCapacitySummary }) {
  const { approvedLostDays, pendingLostDays, lostPersonDays } = capacity;

  return (
    <div className="flex flex-wrap items-stretch gap-x-3 gap-y-3">
      <Term
        label="On paper"
        value={capacity.nominalPersonDays}
        unit="person-days"
        note={`${capacity.workingDayCount} working day${capacity.workingDayCount === 1 ? '' : 's'} × ${capacity.headcount} ${capacity.headcount === 1 ? 'person' : 'people'}`}
      />
      <Operator>−</Operator>
      <Term
        label="To leave"
        value={lostPersonDays}
        unit={lostPersonDays === 1 ? 'person-day' : 'person-days'}
        note={
          lostPersonDays === 0
            ? 'nobody away'
            : `${approvedLostDays} approved · ${pendingLostDays} pending`
        }
        tone={lostPersonDays > 0 ? 'loss' : undefined}
      />
      <Operator>=</Operator>
      <Term
        label="Actually had"
        value={capacity.actualPersonDays}
        unit="person-days"
        note="distinct days, not a sum of requests"
        emphasis
      />

      <div className="ml-auto flex flex-col justify-center rounded-lg border border-border bg-card px-4 py-3 text-right">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Available
        </p>
        <p className="text-3xl font-semibold tabular-nums leading-tight text-foreground">
          {capacity.availabilityPct === null ? '—' : `${capacity.availabilityPct}%`}
        </p>
        <p className="text-[11px] tabular-nums text-muted-foreground">
          {capacity.actualPersonDays} of {capacity.nominalPersonDays}
        </p>
      </div>
    </div>
  );
}

function Operator({ children }: { children: React.ReactNode }) {
  return (
    <span aria-hidden className="self-center text-lg text-muted-foreground/60">
      {children}
    </span>
  );
}

function Term({
  label,
  value,
  unit,
  note,
  tone,
  emphasis,
}: {
  label: string;
  value: number;
  unit: string;
  note: string;
  tone?: 'loss';
  emphasis?: boolean;
}) {
  return (
    <div className="min-w-[9.5rem] flex-1 rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-2xl font-semibold tabular-nums leading-tight',
          emphasis ? 'text-foreground' : 'text-foreground/90',
        )}
        style={tone === 'loss' && value > 0 ? { color: 'var(--heat-loss)' } : undefined}
      >
        {value}
        <span className="ml-1 text-xs font-normal text-muted-foreground">{unit}</span>
      </p>
      <p className="text-[11px] text-muted-foreground">{note}</p>
    </div>
  );
}

// ─── The meter ────────────────────────────────────────────────────────────

/**
 * The same three terms as one bar, which is also the strip's legend.
 *
 * Teaching solid-versus-hatched here means the day grid below needs no key of
 * its own, and the reader meets the distinction attached to numbers they have
 * just read rather than as an abstract swatch.
 */
function CapacityMeter({ capacity }: { capacity: SprintCapacitySummary }) {
  const total = capacity.nominalPersonDays;
  const share = (n: number) => `${(n / total) * 100}%`;

  return (
    <div>
      <div
        className="flex h-2.5 w-full overflow-hidden rounded-[3px] bg-muted"
        role="img"
        aria-label={`${capacity.actualPersonDays} of ${capacity.nominalPersonDays} person-days available; ${capacity.approvedLostDays} lost to approved leave and ${capacity.pendingLostDays} to pending leave`}
      >
        <span className="h-full bg-hygge" style={{ width: share(capacity.actualPersonDays) }} />
        <span
          className="h-full"
          style={{ width: share(capacity.approvedLostDays), backgroundColor: 'var(--heat-loss)' }}
        />
        <span className="h-full" style={{ width: share(capacity.pendingLostDays), ...HATCH }} />
      </div>

      <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <LegendKey className="bg-hygge">Available</LegendKey>
        <LegendKey style={{ backgroundColor: 'var(--heat-loss)' }}>Approved leave</LegendKey>
        <LegendKey style={HATCH}>Pending leave — still declinable</LegendKey>
      </p>
    </div>
  );
}

function LegendKey({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className={cn('h-2.5 w-2.5 shrink-0 rounded-[2px]', className)}
        style={style}
      />
      {children}
    </span>
  );
}

/**
 * Pending leave, drawn as hatching.
 *
 * The texture, not the colour, is what separates it from approved: the two
 * states must stay apart on a greyscale print and for a reader who cannot
 * distinguish the hue at all.
 */
const HATCH: React.CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(135deg, var(--heat-loss) 0 2px, transparent 2px 5px)',
  boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--heat-loss) 45%, transparent)',
};

// ─── The per-person strip ─────────────────────────────────────────────────

const DAY_LABEL: Record<SprintCapacityDayState, string> = {
  present: 'at work',
  approved: 'approved leave',
  pending: 'pending leave',
};

function DayStrip({
  sprint,
  capacity,
}: {
  sprint: OverviewSprint;
  capacity: SprintCapacitySummary;
}) {
  const formatName = useEmployeeNameFormatter();

  /** Open issues and points per person — by status CATEGORY; see the module. */
  const workload = useMemo(() => sprintWorkload(sprint.issues), [sprint.issues]);

  const people = capacity.people;
  const days = capacity.workingDays;

  if (people === null) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-4 text-xs text-muted-foreground">
        The day-by-day breakdown names who is away and when, so it needs the Absences “view
        requests” permission. The totals above are a property of the sprint and are shown to anyone
        who can open the board.
      </p>
    );
  }

  if (people.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-left">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-background pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Person
            </th>
            <th className="whitespace-nowrap pb-1 pr-4 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Open work
            </th>
            {/* The denominator lives in the column header, so the strip can never
                be read as a fortnight when it is ten working days. */}
            <th
              colSpan={days.length}
              className="whitespace-nowrap pb-1 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
            >
              {days.length} working day{days.length === 1 ? '' : 's'}
            </th>
            <th className="whitespace-nowrap pb-1 pl-4 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Away
            </th>
          </tr>
          <tr>
            <th className="sticky left-0 z-10 bg-background" />
            <th />
            {days.map((day) => (
              <th
                key={day}
                scope="col"
                className="px-px pb-1 text-center text-[9px] font-normal leading-none tabular-nums text-muted-foreground/70"
                title={formatDate(day, 'long')}
              >
                <span className="block">{formatDate(day, { weekday: 'narrow' })}</span>
                <span className="block">{formatDate(day, { day: 'numeric' })}</span>
              </th>
            ))}
            <th className="pl-4" />
          </tr>
        </thead>
        <tbody>
          {people.map((person) => {
            const work = workload.get(person.id);
            return (
              <tr key={person.id} className="group">
                <th
                  scope="row"
                  className="sticky left-0 z-10 max-w-[14rem] truncate bg-background py-1 pr-3 text-[13px] font-normal text-foreground"
                >
                  {formatName(person)}
                </th>
                <td className="whitespace-nowrap py-1 pr-4 text-right text-[11px] tabular-nums text-muted-foreground">
                  {work ? (
                    <>
                      {work.open} open
                      {work.points > 0 && <> · {work.points} pts</>}
                    </>
                  ) : (
                    <span className="text-muted-foreground/50">none open</span>
                  )}
                </td>
                {person.days.map((state, i) => (
                  <td key={days[i]} className="px-px py-1 align-middle">
                    <span
                      className={cn(
                        'block h-4 w-full min-w-[0.8rem] rounded-[2px]',
                        state === 'present' && 'bg-muted',
                      )}
                      style={
                        state === 'approved'
                          ? { backgroundColor: 'var(--heat-loss)' }
                          : state === 'pending'
                            ? HATCH
                            : undefined
                      }
                      title={`${formatName(person)} · ${formatDate(days[i], 'long')} · ${DAY_LABEL[state]}`}
                    />
                    {state !== 'present' && (
                      <span className="sr-only">
                        {formatDate(days[i], 'monthDay')}: {DAY_LABEL[state]}
                      </span>
                    )}
                  </td>
                ))}
                <td className="whitespace-nowrap py-1 pl-4 text-right text-[11px] tabular-nums">
                  {person.lostDays === 0 ? (
                    <span className="text-muted-foreground/50">—</span>
                  ) : (
                    <span style={{ color: 'var(--heat-loss)' }}>
                      {person.lostDays}
                      <span className="text-muted-foreground">
                        {person.approvedDays > 0 && person.pendingDays > 0
                          ? ` (${person.approvedDays}a ${person.pendingDays}p)`
                          : person.pendingDays > 0
                            ? ' pending'
                            : ''}
                      </span>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="mt-3 flex items-start gap-1.5 border-t border-border/60 pt-2 text-[10px] leading-relaxed text-muted-foreground">
        <Database className="mt-px h-3 w-3 shrink-0 opacity-60" />
        <span>
          Working days only, weekends excluded — no public-holiday calendar exists in this product,
          so a public holiday counts as a working day here. Leave counts from both places this
          product keeps it: requests booked here and leave synced from PeopleForce, the same pair
          the Absences calendar shows. A day covered by two overlapping requests is counted once.
          Open work counts issues whose status category is neither Done nor Cancelled; points on an
          issue with several assignees count for each of them.
        </span>
      </p>
    </div>
  );
}
